import useLabelledAreas from 'hooks/useLabelledAreas';
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { ImageOverlay, Map as LeafletMap } from 'react-leaflet';
import L, { LeafletEvent, ZoomAnimEvent } from 'leaflet';
import { IAppState } from 'types/app';
import getStateAwareAreas from 'selectors/map/getStateAwareAreas';
import {
  getMinimumZoomThatFits,
  isLodgings,
  isSubLodgings,
  shouldAppearOnMap,
  xy,
} from 'components/Map/util';
import CRS from '../PixiMap/CRS';
import DistrictLabelLayer from 'components/Map/DistrictLabelLayer';
import { IArea, IStateAwareArea } from 'types/map';
import Lodgings from 'components/Map/Lodgings/Lodgings';
import PlayerMarkers from 'components/Map/PlayerMarkers';
import { ModalTooltip } from 'components/ModalTooltip/ModalTooltipContainer';
import MapModalTooltipContext from '../MapModalTooltipContext';
import { MAP_BASE_URL, MAP_HEIGHT, MAP_WIDTH, MIN_ZOOM_LEVEL_FOR_DESTINATIONS } from 'components/Map/constants';
import Limbo from 'components/Map/Limbo';
import { ITooltipData } from 'components/ModalTooltip/types';
import FunnellingIndicator from 'components/Map/FallbackMap/FunnellingIndicator';
import CompatibilityWarning from 'components/Map/FallbackMap/CompatibilityWarning';
import { ZoomControl } from '../PixiMap/ZoomControl';

interface Props extends ReturnType<typeof mapStateToProps> {
  areas: IStateAwareArea[],
  currentArea: IArea,
  initialCenter: number[],
  initialZoom: number,
  onAreaClick: (e: any, area: IArea) => Promise<void>,
  onAreaSelect: (area?: IArea | undefined) => void,
  onClick: (_: { latlng: any }) => any,
  onHitboxClick: (area: IArea) => void,
  onMoveEnd: (e: LeafletEvent) => void,
  onZoomEnd: (e: ZoomAnimEvent) => void,
  selectedArea?: IStateAwareArea | undefined,
  zoomLevel: number,
}

export function FallbackMap(props: Props) {
  const {
    areas,
    currentArea,
    initialCenter,
    onAreaClick,
    onAreaSelect,
    onMoveEnd,
    onZoomEnd,
    selectedArea,
    zoomLevel: parentZoomLevel,
  } = props;

  const minZoom = Math.max(3.0, getMinimumZoomThatFits(window));
  const maxZoom = Math.max(3.6, minZoom);

  const [isModalTooltipOpen, setIsModalTooltipOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState({});
  const [zoomLevel, setZoomLevel] = useState(parentZoomLevel);

  const handleOpenModalTooltip = useCallback((tooltipData: ITooltipData) => {
    setIsModalTooltipOpen(true);
    setTooltipData(tooltipData);
  }, []);

  const handleRequestCloseModalTooltip = useCallback(() => {
    setIsModalTooltipOpen(false);
    onAreaSelect();
  }, [onAreaSelect]);

  const handleSetZoomLevelWithZoomControl = useCallback((_: number, direction?: 'in' | 'out') => {
    switch (direction) {
      case 'in':
        setZoomLevel(maxZoom);
        return;
      case 'out':
        setZoomLevel(minZoom);
        return;
      default:
        return;
    }
  }, [maxZoom, minZoom]);

  const handleZoomEnd = useCallback((e: any) => {
    setZoomLevel(e.target.getZoom());
    onZoomEnd(e);
  }, [onZoomEnd]);

  const isPlayerInLimbo = React.useMemo(() => {
    return !isLodgings(currentArea) && !isSubLodgings(currentArea, areas || []) && !shouldAppearOnMap(currentArea);
  }, [areas, currentArea]);

  const labelledAreas = useLabelledAreas(areas);

  return (
    <MapModalTooltipContext.Provider
      value={{
        onRequestClose: handleRequestCloseModalTooltip,
        openModalTooltip: handleOpenModalTooltip,
      }}
    >
      <LeafletMap
        attributionControl={false}
        center={xy(initialCenter[0], initialCenter[1])}
        maxBounds={L.latLngBounds(
          xy(0, 0),
          xy(MAP_WIDTH, -MAP_HEIGHT),
        )}
        minZoom={minZoom}
        maxZoom={maxZoom}
        maxBoundsViscosity={1}
        onmoveend={onMoveEnd}
        onzoomend={handleZoomEnd}
        zoomControl={false}
        zoom={zoomLevel}
        zoomDelta={0.6}
        zoomSnap={0.6}
        crs={CRS}
      >
        <ImageOverlay
          url={`${MAP_BASE_URL}/fallback/fallback.jpg`}
          bounds={L.latLngBounds(
            xy(0, 0),
            xy(MAP_WIDTH, -MAP_HEIGHT),
          )}
        />

        <DistrictLabelLayer
          areas={labelledAreas.filter(a => a.isDistrict && a.shouldAppearOnMap)}
          currentArea={currentArea}
          onAreaClick={onAreaClick}
          onAreaSelect={onAreaSelect}
          onTapAtLowZoomLevel={() => {
          }}
          zoomLevel={MIN_ZOOM_LEVEL_FOR_DESTINATIONS} // Lie to the markers in order to force them to appear
          fallback
        />

        <DistrictLabelLayer
          areas={labelledAreas.filter(a => !a.isDistrict && a.shouldAppearOnMap)}
          currentArea={currentArea}
          onAreaClick={onAreaClick}
          onAreaSelect={onAreaSelect}
          onTapAtLowZoomLevel={() => {
          }}
          zoomLevel={MIN_ZOOM_LEVEL_FOR_DESTINATIONS} // Lie to the markers in order to force them to appear
        />

        <PlayerMarkers />

        {isPlayerInLimbo && (<Limbo />)}

        <Lodgings
          onAreaClick={onAreaClick}
          onAreaSelect={onAreaSelect}
          selectedArea={selectedArea}
          fallback
        />

        <ModalTooltip
          modalIsOpen={isModalTooltipOpen}
          onRequestClose={handleRequestCloseModalTooltip}
          tooltipData={tooltipData}
          disableTouchEvents
        />

        <FunnellingIndicator />

        <CompatibilityWarning />

        <ZoomControl
          maxZoom={maxZoom}
          setZoomLevel={handleSetZoomLevelWithZoomControl}
          minZoom={minZoom}
          spriteLoaderProgress={100}
          zoomDelta={.6}
          zoomLevel={zoomLevel}
        />

      </LeafletMap>
    </MapModalTooltipContext.Provider>
  );
}

function mapStateToProps(state: IAppState) {
  return {
    areas: getStateAwareAreas(state),
  };
}

export default connect(mapStateToProps)(FallbackMap);